/**
 * In-cart zip field (cart drawer footer). Lets a shopper enter a delivery zip so the
 * free-shipping progress bar has a validated zip to work from. All zip logic lives in
 * window.StockedDeliveryZones; this element is only the cart UI for it.
 *
 * It does NOT gate checkout — evergreen gates the checkout button on the delivery date.
 * As a custom element it re-initializes automatically when the cart drawer re-renders.
 */
(() => {
  class CartZipValidator extends HTMLElement {
    connectedCallback() {
      if (!window.StockedDeliveryZones) return;
      this.input = this.querySelector('.cart-zip-validator__input');
      this.button = this.querySelector('.cart-zip-validator__button');
      this.message = this.querySelector('.cart-zip-validator__message');
      if (!this.input || !this.button || !this.message) return;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onZipCleared = this.onZipCleared.bind(this);

      this.button.addEventListener('click', this.onClick);
      this.input.addEventListener('keydown', this.onKeydown);
      this.input.addEventListener('input', this.onInput);
      document.addEventListener('zipcode:cleared', this.onZipCleared);

      this.prefillFromStorage();
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
      if (this.input) {
        this.input.removeEventListener('keydown', this.onKeydown);
        this.input.removeEventListener('input', this.onInput);
      }
      document.removeEventListener('zipcode:cleared', this.onZipCleared);
    }

    onClick() {
      this.validate();
    }

    onKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.validate();
      }
    }

    onInput() {
      const cleaned = window.StockedDeliveryZones.normalizeZip(this.input.value);
      if (this.input.value !== cleaned) this.input.value = cleaned;
      this.clearMessage();
      this.input.classList.remove('error');
    }

    onZipCleared() {
      this.input.value = '';
      this.clearMessage();
      this.input.classList.remove('error');
    }

    validate() {
      const zones = window.StockedDeliveryZones;
      const zip = zones.normalizeZip(this.input.value);

      if (!zip) {
        this.showError('Please enter a zip code.');
        this.input.focus();
        return;
      }
      if (zip.length !== 5) {
        this.showError('Please enter a valid 5-digit zip code.');
        this.input.focus();
        return;
      }
      if (!Array.isArray(window.deliveryZones)) {
        this.showError('Unable to verify zip code. Please try again.');
        return;
      }
      if (zones.isServiceable(zip)) {
        zones.saveZip(zip);
        this.showSuccess('Stocked delivers to your area.');
      } else {
        this.showError(`Stocked doesn't deliver to ${zip} yet.`);
      }
    }

    prefillFromStorage() {
      const zones = window.StockedDeliveryZones;
      const saved = zones.getSavedZip();
      if (!saved) return;
      if (zones.isServiceable(saved)) {
        this.input.value = saved;
        this.showSuccess('Stocked delivers to your area.');
      } else {
        // Stale/unserviceable saved zip — clear it instead of showing success.
        zones.clearZip();
      }
    }

    showSuccess(text) {
      this.message.textContent = text;
      this.message.className = 'cart-zip-validator__message success';
      this.input.classList.remove('error');
    }

    showError(text) {
      this.message.textContent = text;
      this.message.className = 'cart-zip-validator__message error';
      this.input.classList.add('error');
    }

    clearMessage() {
      this.message.textContent = '';
      this.message.className = 'cart-zip-validator__message';
    }
  }

  if (!customElements.get('cart-zip-validator')) {
    customElements.define('cart-zip-validator', CartZipValidator);
  }
})();
